import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  BaseExceptions,
  getMessageFromErrorResponse,
  isBaseError,
} from '@/api/v4/base-fetch';
import {
  shareProject as shareProjectAPI,
  type ShareProjectBody,
} from '@/api/v4/projects.api';
import { QueryKey } from '@/config/query-client';
import { notify } from '@/ui/snackbar/notify';

interface Payload extends ShareProjectBody {
  projectId: string;
}

export const useShareProject = () => {
  const { t } = useTranslation('default');
  const queryClient = useQueryClient();

  const shareProjectMutation = useMutation({
    mutationFn: async (payload: Payload) => {
      const { projectId, ...data } = payload;
      return shareProjectAPI(projectId, data);
    },
    onSuccess: async (_, variables) => {
      const { projectId } = variables;

      notify({ message: t`share.shareProjectSuccess`, withIcon: true });

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Project, projectId],
        }),
        queryClient.invalidateQueries({ queryKey: [QueryKey.TeamOptions] }),
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Users, projectId],
        }),
      ]);

      queryClient.removeQueries({ queryKey: [QueryKey.TeamOptions] });
      queryClient.removeQueries({ queryKey: [QueryKey.Users, projectId] });
    },
    onError: (error: unknown) => {
      if (isBaseError(error)) {
        if (error.response.status === BaseExceptions.BAD_REQUEST) {
          const message = getMessageFromErrorResponse(error);
          notify({ message: message ?? t`unknownError` });
          return;
        }
      }
      notify({ message: t`unknownError` });
    },
  });

  const shareProject = (payload: Payload) =>
    shareProjectMutation.mutateAsync(payload);

  return {
    shareProject,
    isError: shareProjectMutation.isError,
  };
};
